// src/components/shop/ShopPageClient.tsx
"use client";

import React, { useEffect, useMemo, useState } from "react";
import ShopGridClient, { type Product, type ProductCat } from "./ShopGridClient";

type Props = {
  data: unknown;
  lang: string;
  initialProducts?: Product[];
  initialCategories?: ProductCat[];
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function getLinkFromData(data: unknown): string | undefined {
  if (!isRecord(data)) return undefined;
  const link = data.link;
  return typeof link === "string" ? link : undefined;
}

export default function ShopPageClient({
  data,
  lang,
  initialProducts = [],
  initialCategories = [],
}: Props) {
  const link = getLinkFromData(data);

  const basePath = useMemo(() => {
    let bp = `/${lang}`;
    if (link) {
      try {
        const path = new URL(link, "https://example.local").pathname;
        // Strip WP subpath prefix like /pt-api if present
        const cleaned = path.replace(/^\/[a-z]{2}-api(?=\/|$)/i, "");
        // Ensure the path starts with /${lang}
        if (cleaned === `/${lang}` || cleaned.startsWith(`/${lang}/`)) {
          bp = cleaned;
        } else {
          const rest = cleaned.startsWith("/") ? cleaned : `/${cleaned}`;
          bp = `/${lang}${rest}`;
        }
      } catch {
        // keep default
      }
    }
    return bp;
  }, [link, lang]);

  const [selectedCatSlug, setSelectedCatSlug] = useState<string>("all");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const usp = new URLSearchParams(window.location.search);
    setSelectedCatSlug(usp.get("cat") || "all");
  }, []);

  return (
    <ShopGridClient
      items={initialProducts}
      basePath={basePath}
      lang={lang}
      categories={initialCategories}
      selectedCatSlug={selectedCatSlug}
    />
  );
}